import { useState, useEffect } from "react";
import axios from "axios";
import { ArrowRight, RotateCcw, Mail, CheckCircle2, ClipboardList } from "lucide-react";
import { Button } from "./ui/button";
import { useLanguage } from "../context/LanguageContext";
import { useTranslation } from "../translations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_STYLES = [
  { text: "text-red-500", bg: "bg-red-500", ring: "#EF4444" },
  { text: "text-amber-500", bg: "bg-amber-500", ring: "#F59E0B" },
  { text: "text-[#0047AB]", bg: "bg-[#0047AB]", ring: "#0047AB" },
  { text: "text-[#10B981]", bg: "bg-emerald-500", ring: "#10B981" },
];

const levelIndex = (score) => (score <= 25 ? 0 : score <= 50 ? 1 : score <= 75 ? 2 : 3);

const LEVEL_NAMES = ["Emerging", "Establishing", "Consistent", "Multiplying"];
const LEVEL_COLORS = ["#DBEAFE", "#93C5FD", "#0047AB", "#1E3A8A"];

const LevelStaircase = ({ lvl }) => (
  <div className="flex items-end justify-center gap-2 max-w-md mx-auto" data-testid="diagnostic-staircase">
    {LEVEL_NAMES.map((name, i) => (
      <div key={i} className="flex-1 text-center">
        <div
          className={`rounded-t-lg transition-all duration-700 ${i === lvl ? "ring-2 ring-offset-2 ring-[#0047AB]" : "opacity-50"}`}
          style={{ height: 24 + i * 16, backgroundColor: LEVEL_COLORS[i] }}
        />
        <p className={`text-[10px] mt-1.5 mono-cayus ${i === lvl ? "text-[#1d4ed8] font-semibold" : "text-[#9098A3]"}`}>{name}</p>
      </div>
    ))}
  </div>
);

const DiagnosticSection = () => {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const d = t.diagnostic;

  const [stage, setStage] = useState("intro");
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [diagId, setDiagId] = useState(null);
  const [email, setEmail] = useState("");
  const [emailStatus, setEmailStatus] = useState("idle");

  const score = Math.round((answers.reduce((a, b) => a + b, 0) / (d.questions.length * 3)) * 100);
  const lvl = levelIndex(score);
  const style = LEVEL_STYLES[lvl];

  const restart = () => {
    setStage("intro");
    setQIdx(0);
    setAnswers([]);
    setSelected(null);
    setDiagId(null);
    setEmail("");
    setEmailStatus("idle");
  };

  useEffect(restart, [language]);

  const pickOption = (idx, value) => {
    if (selected !== null) return;
    setSelected(idx);
    const newAnswers = [...answers, value];
    setTimeout(() => {
      setAnswers(newAnswers);
      setSelected(null);
      if (qIdx + 1 < d.questions.length) {
        setQIdx(qIdx + 1);
      } else {
        const finalScore = Math.round((newAnswers.reduce((a, b) => a + b, 0) / (d.questions.length * 3)) * 100);
        setStage("results");
        axios
          .post(`${API}/diagnostic`, {
            answers: newAnswers,
            score: finalScore,
            level: d.results.levels[levelIndex(finalScore)].name,
            language,
          })
          .then((res) => setDiagId(res.data.id))
          .catch(() => {});
      }
    }, 350);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    if (!email || emailStatus === "loading") return;
    setEmailStatus("loading");
    try {
      await axios.put(`${API}/diagnostic/${diagId}/email`, {
        email,
        dimensions: d.questions.map((q, i) => ({ label: q.dimension, score: answers[i] })),
        level_description: d.results.levels[lvl].description,
      });
      setEmailStatus("success");
    } catch {
      setEmailStatus("idle");
    }
  };

  const scrollToDemo = () => {
    const el = document.querySelector("#demo-form");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="diagnostic" className="bg-[#F7F9FC] py-14 lg:py-20" data-testid="diagnostic-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <span className="text-sm font-semibold text-[#0047AB] uppercase tracking-widest mb-4 block">
            {d.tagline}
          </span>
          <h2 className="font-bold text-3xl sm:text-4xl text-[#1B1B1B] tracking-tight mb-4">
            {d.headline}
          </h2>
          <p className="text-lg text-[#4D4D4D] leading-relaxed max-w-2xl mx-auto">{d.subheadline}</p>
        </div>

        <div className="bg-white border border-[#D9D9D9] rounded-2xl shadow-sm p-6 sm:p-8 min-h-[380px] flex flex-col justify-center">
          {stage === "intro" && (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#F3F8FE] border border-[#E4EEFC] mb-5">
                <ClipboardList className="w-7 h-7 text-[#0047AB]" />
              </div>
              <div className="flex items-center justify-center gap-2 mb-6 flex-wrap">
                {d.questions.map((q, i) => (
                  <span key={i} className="text-xs bg-[#F3F3F3] text-[#5E6C83] rounded-full px-3 py-1">{q.dimension}</span>
                ))}
              </div>
              <Button
                onClick={() => setStage("quiz")}
                className="rounded-full px-8 py-4 bg-[#0047AB] text-white font-semibold hover:bg-[#0B3F8F] transition-all cta-glow"
                data-testid="diagnostic-start"
              >
                {d.startCta}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          )}

          {stage === "quiz" && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-[#0047AB] uppercase tracking-wide">
                  {d.questions[qIdx].dimension}
                </span>
                <span className="text-xs text-[#9098A3]">
                  {d.questionLabel} {qIdx + 1}/{d.questions.length}
                </span>
              </div>
              <div className="w-full bg-[#F3F3F3] rounded-full h-1.5 mb-6">
                <div
                  className="bg-[#0047AB] h-1.5 rounded-full transition-all duration-500"
                  style={{ width: `${((qIdx + (selected !== null ? 1 : 0)) / d.questions.length) * 100}%` }}
                />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-[#1B1B1B] mb-5" data-testid="diagnostic-question">
                {d.questions[qIdx].question}
              </h3>
              <div className="flex flex-col gap-2.5">
                {d.questions[qIdx].options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => pickOption(i, opt.v)}
                    className={`w-full text-left px-4 py-3 rounded-xl border text-sm sm:text-[15px] transition-all duration-200 ${
                      selected === i
                        ? "border-[#0047AB] bg-[#F3F8FE] text-[#1d4ed8] font-medium"
                        : "border-[#D9D9D9] bg-white text-[#3A3F4B] hover:border-blue-300 hover:bg-[#F3F8FE]/40"
                    }`}
                    data-testid={`diagnostic-option-${i}`}
                  >
                    {opt.t}
                  </button>
                ))}
              </div>
            </div>
          )}

          {stage === "results" && (
            <div>
              <p className="text-center text-sm font-semibold text-[#5E6C83] uppercase tracking-widest mb-4">
                {d.results.title}
              </p>
              <LevelStaircase lvl={lvl} />
              <p className={`text-center text-2xl font-bold mt-3 ${style.text}`} data-testid="diagnostic-level">
                {d.results.levels[lvl].name}
              </p>
              <p className="text-center text-sm text-[#4D4D4D] leading-relaxed max-w-lg mx-auto mt-2 mb-6">
                {d.results.levels[lvl].description}
              </p>

              <p className="text-xs font-semibold text-[#5E6C83] uppercase tracking-wide mb-3">{d.results.dimensionsTitle}</p>
              <div className="flex flex-col gap-2.5 mb-7">
                {d.questions.map((q, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs text-[#5E6C83] w-36 sm:w-44 flex-shrink-0 truncate">{q.dimension}</span>
                    <div className="flex-1 bg-[#F3F3F3] rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${style.bg} transition-all duration-1000`}
                        style={{ width: `${(answers[i] / 3) * 100}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {emailStatus === "success" ? (
                <div className="flex items-center justify-center gap-2 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 mb-5" data-testid="diagnostic-email-success">
                  <CheckCircle2 className="w-4 h-4 text-[#10B981]" />
                  <p className="text-sm text-emerald-700 font-medium">{d.results.emailSuccess}</p>
                </div>
              ) : (
                <form onSubmit={submitEmail} className="mb-5">
                  <p className="text-sm font-semibold text-[#3A3F4B] mb-2 flex items-center gap-1.5">
                    <Mail className="w-4 h-4 text-[#0047AB]" />
                    {d.results.emailTitle}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={d.results.emailPlaceholder}
                      className="flex-1 px-4 py-2.5 rounded-full border border-[#D9D9D9] text-sm focus:outline-none focus:border-[#0047AB] focus:ring-2 focus:ring-blue-100"
                      data-testid="diagnostic-email-input"
                    />
                    <Button
                      type="submit"
                      disabled={emailStatus === "loading"}
                      className="rounded-full px-5 py-2.5 bg-[#0047AB] text-white text-sm font-semibold hover:bg-[#0B3F8F]"
                      data-testid="diagnostic-email-submit"
                    >
                      {emailStatus === "loading" ? "..." : d.results.emailCta}
                    </Button>
                  </div>
                </form>
              )}

              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2 border-t border-[#ECECEC]">
                <button
                  onClick={restart}
                  className="flex items-center gap-1.5 text-sm text-[#9098A3] hover:text-[#4D4D4D] transition-colors mt-3"
                  data-testid="diagnostic-restart"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  {d.results.restartCta}
                </button>
                <button
                  onClick={scrollToDemo}
                  className="flex items-center gap-1.5 text-sm font-semibold text-[#0047AB] hover:text-[#0B3F8F] transition-colors mt-3"
                  data-testid="diagnostic-demo-cta"
                >
                  {d.results.demoCta}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default DiagnosticSection;
